// Pane-global registry mapping live DOM tile elements to their drag-and-drop behavior,
// so the coordinate-only Office.js DragAndDropEvent (New Outlook / OWA) can be routed to
// the correct tile — the parity bridge between the pane-global event and the per-tile DOM
// drag/drop model that desktop-file drops already use.
//
// Two kinds of targets mirror the two DOM affordances:
// - droppable (onDrop present, canDrop() true): the blue "you can drop here" surface.
// - hover-only (no onDrop, or canDrop() false, with canHover() true): the grey
//   "hovering does something" surface — sites/Tess tiles that auto-expand on hover but
//   reject drops (the DOM path's dropEffect="none" tiles).
// Both kinds surface hover through onDragHoverChange, which tiles wire to the SAME
// isDraggedOver state their DOM handlers set — so the border styles, grey/blue choice,
// and the 1500 ms auto-expand / breadcrumb-navigate timers run identically on both paths.
export interface OfficeDropTarget {
    element: HTMLElement;
    // Runs AFTER the pane-global handler has already staged the dropped bytes; performs
    // only this tile's save-navigation (same as the tile's DOM onDrop post-staging step).
    // Absent on hover-only targets (expand affordance without drop support).
    onDrop?: () => void;
    // Whether this target currently accepts a drop (mirrors the tile's isValidDropZone).
    canDrop?: () => boolean;
    // Hover parity: invoked with true/false as the Office.js drag enters/leaves this
    // target, mirroring the DOM dragEnter/dragLeave that drive the tile's isDraggedOver.
    onDragHoverChange?: (isOver: boolean) => void;
    // Whether this target is hover-interactive when it is not currently droppable.
    // Defaults to droppability — pass () => true for tiles whose DOM path always shows
    // an affordance (grey when the drop is disabled), e.g. main locations, breadcrumbs,
    // and the expand-only site/Tess tiles.
    canHover?: () => boolean;
}

const targets = new Set<OfficeDropTarget>();

export function isDroppableTarget(target: OfficeDropTarget): boolean {
    return !!target.onDrop && (target.canDrop ? target.canDrop() : true);
}

function isHoverableTarget(target: OfficeDropTarget): boolean {
    if (!target.onDragHoverChange) {
        return false;
    }
    return target.canHover ? target.canHover() : isDroppableTarget(target);
}

function isInteractiveTarget(target: OfficeDropTarget): boolean {
    return isDroppableTarget(target) || isHoverableTarget(target);
}

export function registerOfficeDropTarget(target: OfficeDropTarget): () => void {
    targets.add(target);
    return () => {
        targets.delete(target);
        // A hovered target can unregister mid-drag: its root element swaps (conditional
        // re-render, list re-keying after an auto-expand) or the component unmounts
        // (e.g. breadcrumb hover-navigate replacing the view). Release the hover THROUGH
        // the callback — once `hovered` is null, clearHover/idle-clear can no longer
        // reach the tile, and a still-mounted component would keep isDraggedOver stuck
        // true (stale border + its hover-expand timer would still fire). On the unmount
        // path the callback is a harmless no-op state update.
        if (hovered === target) {
            hovered = null;
            target.onDragHoverChange?.(false);
        }
    };
}

// Walk up from the topmost element under (x, y); return the FIRST (innermost) ancestor
// that is a registered, interactive target — droppable OR hover-only. Ancestor-walk (not
// a plain contains() scan) so nested tiles resolve to the most specific target. Returning
// hover-only targets is deliberate: a drop landing on one must be swallowed (DOM parity
// with dropEffect="none"), not fall through to an outer droppable surface — callers
// discriminate with isDroppableTarget().
export function resolveOfficeDropTargetAt(x: number, y: number): OfficeDropTarget | null {
    let el: HTMLElement | null = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!el) {
        return null;
    }
    const byElement = new Map<HTMLElement, OfficeDropTarget>();
    targets.forEach(t => byElement.set(t.element, t));
    while (el) {
        const target = byElement.get(el);
        if (target && isInteractiveTarget(target)) {
            return target;
        }
        el = el.parentElement;
    }
    return null;
}

let hovered: OfficeDropTarget | null = null;

// Route the Office.js drag-over at (x, y) to the interactive target under it, flipping
// onDragHoverChange on the entered/left targets. At most one target is hovered at a time
// (there is a single pane-global drag). The tile renders its own affordance from the
// resulting isDraggedOver state — blue dropzoneActive or grey dragzoneActive, exactly as
// the DOM path does — so there is no imperative styling here.
export function hoverTargetAt(x: number, y: number): void {
    const next = resolveOfficeDropTargetAt(x, y);
    if (next === hovered) {
        return;
    }
    hovered?.onDragHoverChange?.(false);
    next?.onDragHoverChange?.(true);
    hovered = next;
}

export function clearHover(): void {
    if (hovered) {
        hovered.onDragHoverChange?.(false);
        hovered = null;
    }
}
